/**
 * Invoicing block for the CRM customer-360 detail (crm.js).
 *
 * Shows, for one customer: the next scheduled invoice run (if any), a quick
 * "schedule a run" form, and the list of invoices/receipts already generated.
 * Keyed on the customer's Porta id (from the detail response) — customers with
 * no billing account can't be invoiced, so the block explains that instead.
 *
 * Kept in its own module so crm.js only needs a container + one call.
 */
import { escapeHtml, formatDateTimeShort } from '../../src/lib/format.js';
import { hasRole } from '../../src/lib/roles.js';
import { openInvoicePreview } from './invoice-preview.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const flash = (m, t) => notify.showFlash(m, t);

const EDIT_ROLES = ['root', 'sales_officer', 'sales_supervisor', 'billing_officer', 'billing_supervisor'];
const CHIP_CLASS = {
  Overdue: 'danger', Due: 'warn', Scheduled: '', Processed: 'ok',
  Cancelled: 'muted', 'Awaiting approval': 'warn',
};

function money(cur, v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  return `${cur || 'GMD'} ${Number.isFinite(n) ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v}`;
}

export async function renderCustomerInvoicing(container, item, detail) {
  if (!container) return;
  // Only a numeric porta id is a real PortaBilling link; placeholders
  // (manual-…/PT-…/CLI-…) mean the record isn't linked and can't be invoiced.
  const pidRaw = ((detail && detail.porta_customer_id) ?? '').toString().trim();
  const pid = /^\d+$/.test(pidRaw) ? pidRaw : null;
  const name = (detail && detail.display_name) || (item && item.display_name) || '';
  const segment = (detail && detail.segment) || null;
  const email = (detail && (detail.primary_email || (detail.emails || [])[0])) || null;

  container.innerHTML = `
    <div class="crm-detail-header">
      <strong>Invoicing</strong>
      <span class="crm-detail-sub">Scheduled runs &amp; generated documents</span>
    </div>
    <div class="crm-detail-body" data-role="invoicing-body"><div class="crm-detail-loading">Loading…</div></div>`;
  const body = container.querySelector('[data-role="invoicing-body"]');

  if (pid == null || pid === '') {
    body.innerHTML = '<div class="crm-detail-empty">No billing account linked — link one to schedule invoices.</div>';
    return;
  }
  await loadInvoicing(body, { pid: Number(pid), name, segment, email });
}

async function loadInvoicing(body, ctx) {
  body.innerHTML = '<div class="crm-detail-loading">Loading…</div>';
  let reminders = [];
  let invoices = [];
  let pref = { invoicing_enabled: false };
  try {
    const [remRes, invRes, prefRes] = await Promise.all([
      api.fetch(API.invoicingReminders(`porta_customer_id=${ctx.pid}`), { method: 'GET' }),
      api.fetch(API.invoicingInvoices(`porta_customer_id=${ctx.pid}`), { method: 'GET' }),
      api.fetch(API.invoicingCustomerPreferences(ctx.pid), { method: 'GET' }),
    ]);
    reminders = remRes.items || [];
    invoices = invRes.items || [];
    pref = prefRes || pref;
  } catch (err) {
    body.innerHTML = '<div class="crm-detail-empty">Invoicing isn’t available for your role.</div>';
    return;
  }
  const nextScheduled = reminders.find((r) => r.status === 'scheduled');
  body.innerHTML = optInSection(pref) + actionsSection(nextScheduled) + invoicesSection(invoices);
  wire(body, ctx, invoices);
}

function optInSection(pref) {
  const canEdit = hasRole(...EDIT_ROLES);
  const on = !!(pref && pref.invoicing_enabled);
  const state = on
    ? '<span class="badge ok">Invoicing on</span>'
    : '<span class="badge muted">Invoicing off</span>';
  const toggle = canEdit
    ? `<label style="display:inline-flex; align-items:center; gap:6px; cursor:pointer; margin-left:8px;">
         <input type="checkbox" data-inv-optin ${on ? 'checked' : ''}>
         <span class="crm-detail-sub">${on ? 'Enabled' : 'Enable invoicing'}</span></label>`
    : '';
  const hint = on ? '' : '<div class="crm-detail-sub" style="margin-top:2px;">Scheduling or generating also enables it automatically.</div>';
  return `<div style="margin-bottom:10px;">${state}${toggle}${hint}</div>`;
}

const PERIOD_OPTIONS = `<option value="monthly">Monthly</option><option value="quarterly">Quarterly</option>
  <option value="semiannual">6-monthly</option><option value="annual">Yearly</option>`;

function actionsSection(next) {
  const canEdit = hasRole(...EDIT_ROLES);
  const editBtn = (next && canEdit)
    ? ` <button type="button" class="btn ghost" data-inv-edit-sched style="font-size:11px; padding:2px 8px;">Edit</button>` : '';
  const nextLine = next
    ? `<div style="margin-bottom:6px;">Next run: <strong>${escapeHtml(next.scheduled_for)}</strong>
         <span class="crm-detail-sub">(${escapeHtml(next.period)})</span>
         <span class="badge ${CHIP_CLASS[next.chip] || ''}">${escapeHtml(next.chip)}</span>${editBtn}</div>`
    : '<div class="crm-detail-sub" style="margin-bottom:6px;">No invoice run scheduled.</div>';
  if (!canEdit) return `<div style="margin-bottom:12px;">${nextLine}</div>`;
  const editForm = next
    ? `<form data-inv-edit-form hidden class="row" data-rid="${escapeHtml(next.id)}" data-period="${escapeHtml(next.period || 'monthly')}"
             style="gap:6px; align-items:flex-end; flex-wrap:wrap; margin-bottom:8px; padding:8px; background:var(--panel-2,#f7f7f9); border-radius:8px;">
        <label class="field"><span class="crm-detail-sub">Date</span>
          <input name="scheduled_for" type="date" class="input" value="${escapeHtml(next.scheduled_for || '')}"></label>
        <label class="field"><span class="crm-detail-sub">Period</span>
          <select name="period" class="input">${PERIOD_OPTIONS}</select></label>
        <label class="field" style="flex:1; min-width:160px;"><span class="crm-detail-sub">Note</span>
          <input name="note" type="text" class="input" value="${escapeHtml(next.note || '')}" placeholder="Optional"></label>
        <button type="submit" class="btn primary">Save</button>
        <button type="button" class="btn ghost" data-inv-edit-cancel>Cancel</button>
      </form>` : '';
  return `<div style="margin-bottom:12px;">
    ${nextLine}
    ${editForm}
    <div class="row" style="gap:16px; align-items:flex-end; flex-wrap:wrap;">
      <form data-inv-schedule class="row" style="gap:6px; align-items:flex-end; flex-wrap:wrap;">
        <label class="field"><span class="crm-detail-sub">Schedule a run</span>
          <input name="scheduled_for" type="date" required class="input"></label>
        <label class="field"><span class="crm-detail-sub">Period</span>
          <select name="period" class="input">${PERIOD_OPTIONS}</select></label>
        <button type="submit" class="btn">Schedule</button>
      </form>
      <form data-inv-generate class="row" style="gap:6px; align-items:flex-end; flex-wrap:wrap;">
        <label class="field"><span class="crm-detail-sub">Generate now</span>
          <select name="period" class="input">${PERIOD_OPTIONS}</select></label>
        <label class="field" style="align-self:center;"><span class="crm-detail-sub">&nbsp;</span>
          <span style="white-space:nowrap;"><input type="checkbox" name="vat_on"> VAT</span></label>
        <label class="field" style="max-width:90px;"><span class="crm-detail-sub">VAT %</span>
          <input name="vat_rate" type="number" step="0.01" min="0" max="100" class="input" placeholder="0" disabled></label>
        <label class="field" style="max-width:150px;"><span class="crm-detail-sub">VAT shown</span>
          <select name="vat_mode" class="input" title="Added on top: a separate VAT row above the total. Included in prices: the VAT is folded into the line amounts. The total due is the same either way." disabled>
            <option value="exclusive">Added on top</option>
            <option value="inclusive">Included in prices</option>
          </select></label>
        <label class="field" style="align-self:center;"><span class="crm-detail-sub">&nbsp;</span>
          <span style="white-space:nowrap;" title="Show one line per PortaBilling connection (uncheck for a single combined line)"><input type="checkbox" name="expand_connections" checked> Itemise connections</span></label>
        <button type="submit" class="btn primary" title="Issue an invoice for this customer now">Generate invoice</button>
      </form>
    </div></div>`;
}

function invoicesSection(invoices) {
  if (!invoices.length) return '<div class="crm-detail-empty">No invoices or receipts generated yet.</div>';
  const canEdit = hasRole(...EDIT_ROLES);
  const rows = invoices.map((i) => {
    const isReceipt = i.kind === 'receipt';
    const isVoid = i.status === 'void';
    // Prefer the sent date once sent — this doubles as the "date sent" history.
    const sent = !!i.sent_at;
    const when = sent ? i.sent_at : (i.issued_at || i.sent_at);
    const whenLabel = sent ? 'Sent ' : (i.issued_at ? 'Issued ' : '');
    const statusBadge = isVoid
      ? '<span class="badge muted">Void</span>'
      : (sent ? '<span class="badge ok">Sent</span>' : `<span class="badge">${escapeHtml(i.status || '')}</span>`);
    // Only issued (approved, not yet sent) invoices offer a Send action.
    const canSend = canEdit && !isReceipt && !isVoid && i.status === 'issued' && !i.pending_approval;
    const preview = `<button class="btn" data-inv-preview="${escapeHtml(i.id)}" style="font-size:11px; padding:2px 8px;">Preview</button>`;
    const sendOrNote = canSend
      ? `<button class="btn primary" data-inv-send="${escapeHtml(i.id)}" style="font-size:11px; padding:2px 8px;">Send</button>`
      : (i.pending_approval ? '<span class="crm-detail-sub">awaiting approval</span>' : '');
    // An issued-but-not-sent invoice (a mistake) can be deleted; a sent one is
    // voided instead (kept for audit) so a corrected one can be re-issued.
    const canDelete = canEdit && !isReceipt && !sent && !isVoid;
    const canVoid = canEdit && !isReceipt && sent && !isVoid;
    const del = canDelete
      ? `<button class="btn danger ghost" data-inv-delete="${escapeHtml(i.id)}" title="Delete this unsent invoice" style="font-size:11px; padding:2px 8px;">Delete</button>`
      : (canVoid
        ? `<button class="btn danger ghost" data-inv-void="${escapeHtml(i.id)}" title="Void this invoice so a corrected one can be issued" style="font-size:11px; padding:2px 8px;">Void</button>`
        : '');
    return `<tr>
      <td><span class="badge ${isReceipt ? 'ok' : ''}">${isReceipt ? 'Receipt' : 'Invoice'}</span>
          <strong style="margin-left:4px;">${escapeHtml(i.number || '—')}</strong></td>
      <td>${money(i.currency, i.total)}</td>
      <td>${statusBadge}</td>
      <td class="crm-detail-sub">${escapeHtml(whenLabel)}${escapeHtml(when ? formatDateTimeShort(when) : '')}</td>
      <td style="text-align:right; white-space:nowrap;">${preview} ${sendOrNote} ${del}</td></tr>`;
  }).join('');
  return `<div class="table-wrap"><table class="crm-inbox-table">
    <thead><tr><th>Document</th><th>Total</th><th>Status</th><th>Date</th><th></th></tr></thead>
    <tbody>${rows}</tbody></table></div>`;
}

function wire(body, ctx, invoices = []) {
  const optIn = body.querySelector('[data-inv-optin]');
  if (optIn) {
    optIn.addEventListener('change', async () => {
      optIn.disabled = true;
      try {
        await api.fetch(API.invoicingCustomerPreferences(ctx.pid), {
          method: 'PUT', body: JSON.stringify({ invoicing_enabled: optIn.checked }),
        });
        flash(optIn.checked ? 'Invoicing enabled for this customer.' : 'Invoicing disabled.', 'success');
        await loadInvoicing(body, ctx);
      } catch (err) {
        optIn.checked = !optIn.checked;
        optIn.disabled = false;
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not update.', 'error');
      }
    });
  }

  const scheduleForm = body.querySelector('[data-inv-schedule]');
  if (scheduleForm) {
    scheduleForm.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const date = scheduleForm.scheduled_for.value;
      if (!date) { flash('Pick a date.', 'error'); return; }
      try {
        await api.fetch(API.invoicingReminderCreate(), {
          method: 'POST',
          body: JSON.stringify({
            porta_customer_id: ctx.pid, customer_name: ctx.name || null,
            segment: ctx.segment || null, scheduled_for: date, period: scheduleForm.period.value,
          }),
        });
        flash('Invoice run scheduled.', 'success');
        await loadInvoicing(body, ctx);
      } catch (err) {
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not schedule.', 'error');
      }
    });
  }

  // Edit the scheduled run (date / period / note).
  const editSchedBtn = body.querySelector('[data-inv-edit-sched]');
  const editForm = body.querySelector('[data-inv-edit-form]');
  if (editSchedBtn && editForm) {
    editForm.period.value = editForm.dataset.period || 'monthly';
    editSchedBtn.addEventListener('click', () => { editForm.hidden = !editForm.hidden; });
    editForm.querySelector('[data-inv-edit-cancel]').addEventListener('click', () => { editForm.hidden = true; });
    editForm.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      if (!editForm.scheduled_for.value) { flash('Pick a date.', 'error'); return; }
      try {
        await api.fetch(API.invoicingReminderUpdate(editForm.dataset.rid), {
          method: 'PATCH',
          body: JSON.stringify({
            scheduled_for: editForm.scheduled_for.value, period: editForm.period.value,
            note: editForm.note.value.trim() || null,
          }),
        });
        flash('Schedule updated.', 'success');
        await loadInvoicing(body, ctx);
      } catch (err) {
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not update the schedule.', 'error');
      }
    });
  }

  const generateForm = body.querySelector('[data-inv-generate]');
  if (generateForm) {
    // Enable the VAT % field only when the VAT box is ticked.
    const vatBox = generateForm.querySelector('[name="vat_on"]');
    const vatInput = generateForm.querySelector('[name="vat_rate"]');
    const vatMode = generateForm.querySelector('[name="vat_mode"]');
    if (vatBox && vatInput) {
      vatBox.addEventListener('change', () => {
        vatInput.disabled = !vatBox.checked;
        if (vatMode) vatMode.disabled = !vatBox.checked;
        if (!vatBox.checked) vatInput.value = '';
        else vatInput.focus();
      });
    }
    generateForm.addEventListener('submit', async (ev) => {
      ev.preventDefault();
      const vatRate = (vatBox && vatBox.checked && vatInput && vatInput.value !== '') ? vatInput.value : null;
      if (vatBox && vatBox.checked && !vatRate) { flash('Enter a VAT % or untick VAT.', 'error'); return; }
      const expandBox = generateForm.querySelector('[name="expand_connections"]');
      const expand = expandBox ? expandBox.checked : true;
      const vatHow = (vatRate && vatMode && vatMode.value === 'inclusive') ? ' included in the prices' : ' added on top';
      if (!window.confirm(vatRate ? `Generate an invoice with ${vatRate}% VAT${vatHow} now?` : 'Generate an invoice for this customer now?')) return;
      try {
        const inv = await api.fetch(API.invoicingInvoices(), {
          method: 'POST',
          body: JSON.stringify({
            porta_customer_id: ctx.pid, cadence: generateForm.period.value, delivery: 'email',
            customer_name: ctx.name || `Customer ${ctx.pid}`, segment: ctx.segment || null,
            email: ctx.email || null, vat_rate: vatRate, expand_connections: expand,
            vat_mode: (vatRate && vatMode) ? vatMode.value : 'exclusive',
          }),
        });
        flash(inv && inv.pending_approval
          ? 'Invoice generated — awaiting approval before it can be sent or downloaded.'
          : 'Invoice generated.', 'success');
        // Show the preview so it can be reviewed before sending (or approval).
        openInvoicePreview(inv, { onChanged: () => loadInvoicing(body, ctx) });
        await loadInvoicing(body, ctx);
      } catch (err) {
        const code = err && err.body && err.body.detail;
        flash(code === 'invoicing_not_enabled'
          ? 'This customer hasn’t opted into invoicing yet.'
          : (api.formatErrorDetail(code) || 'Could not generate the invoice.'), 'error');
      }
    });
  }

  body.querySelectorAll('[data-inv-send]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!window.confirm('Send this invoice to the customer now?')) return;
      btn.disabled = true;
      try {
        const res = await api.fetch(API.invoicingSend(btn.dataset.invSend), { method: 'POST' });
        flash(`Invoice ${res && res.number ? res.number + ' ' : ''}sent to the customer.`, 'success');
        await loadInvoicing(body, ctx);
      } catch (err) {
        btn.disabled = false;
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not send the invoice.', 'error');
      }
    });
  });

  body.querySelectorAll('[data-inv-preview]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const inv = invoices.find((i) => i.id === btn.dataset.invPreview);
      if (inv) openInvoicePreview(inv, { onChanged: () => loadInvoicing(body, ctx) });
    });
  });

  body.querySelectorAll('[data-inv-delete]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!window.confirm('Delete this unsent invoice? This cannot be undone.')) return;
      btn.disabled = true;
      try {
        await api.fetch(API.invoicingInvoice(btn.dataset.invDelete), { method: 'DELETE' });
        flash('Invoice deleted.', 'success');
        await loadInvoicing(body, ctx);
      } catch (err) {
        btn.disabled = false;
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not delete the invoice.', 'error');
      }
    });
  });

  body.querySelectorAll('[data-inv-void]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      if (!window.confirm('Void this invoice? It stays on record as voided; you can then generate a corrected one.')) return;
      btn.disabled = true;
      try {
        await api.fetch(API.invoicingVoid(btn.dataset.invVoid), { method: 'POST' });
        flash('Invoice voided. Generate a corrected invoice when ready.', 'success');
        await loadInvoicing(body, ctx);
      } catch (err) {
        btn.disabled = false;
        flash(api.formatErrorDetail(err && err.body && err.body.detail) || 'Could not void the invoice.', 'error');
      }
    });
  });
}

export default { renderCustomerInvoicing };
